import React, { useEffect, useRef, useState } from "react"
import { useStaticQuery, graphql, Link } from "gatsby"
import { WindowLocation } from "@reach/router"

import { Logo } from "./utils"
import List from "./navigation-list"
import { Menu, X, Instagram } from "react-feather"
import SideBar from "./sidebar"
import { NavigationQuery } from "./__generated__/NavigationQuery"
import { Theme } from "./layout"

type NavbarProps = {
    navPlaceholder: boolean
    location: WindowLocation
    currentTheme: number
    switchTheme: () => void
    themes: Theme[]
    allowThemeSwitch: boolean
    front: boolean
}
const Navbar: React.FC<NavbarProps> = ({
    navPlaceholder,
    location,
    currentTheme,
    switchTheme,
    themes,
    allowThemeSwitch = true,
    front,
}) => {
    const currentLocation = location.pathname.split("/")[1]

    const data = useStaticQuery<NavigationQuery>(graphql`
        query NavigationQuery {
            site {
                siteMetadata {
                    title
                    social {
                        name
                        url
                    }
                }
            }
        }
    `)

    const instagram = (data.site.siteMetadata.social || []).find(
        s => s.name.toLowerCase() === "instagram"
    )

    const navbar = useRef(null)

    const [scrolled, changeState] = useState(false)
    const [navbarHeight, setNavbarHeight] = useState(0)
    const [sidebarOpen, setSidebarOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => {
            if (document.documentElement.scrollTop > 40 && !scrolled) {
                changeState(true)
            } else if (document.documentElement.scrollTop <= 40 && scrolled)
                changeState(false)
        }

        window.addEventListener("scroll", onScroll)

        setNavbarHeight(navbar.current.getBoundingClientRect().height)

        return () => {
            window.removeEventListener("scroll", onScroll)
        }
    }, [scrolled])

    // Transparent, light-on-dark treatment while sitting over the hero image.
    const overHero = front && !scrolled

    return (
        <React.Fragment>
            <header
                className={`nav fixed w-full z-50 ${
                    scrolled ? "scrolled bg-bg" : ""
                } ${overHero ? "over-hero" : !scrolled ? "bg-bg" : ""}`}
                ref={navbar}
            >
                <div
                    className={`large-container flex items-center justify-between transition-all duration-500 ${
                        scrolled ? "py-3" : "py-5 lg:py-7"
                    }`}
                >
                    <button
                        className="nav-icon text-color-1 outline-none lg:hidden"
                        aria-label="Menu"
                        onClick={() => {
                            setSidebarOpen(true)
                        }}
                    >
                        <Menu size={22} strokeWidth={1.25} />
                    </button>

                    <Link
                        to="/"
                        title={data.site.siteMetadata.title}
                        className="lg:flex-none"
                    >
                        <Logo
                            className={`brand-mark ${
                                scrolled ? "w-16 lg:w-16" : "w-16 lg:w-20"
                            }`}
                        />
                    </Link>

                    <div className="hidden lg:flex items-center">
                        <List
                            name="navbar"
                            className="nav-links flex items-center"
                            current={currentLocation}
                            currentTheme={currentTheme}
                            switchTheme={switchTheme}
                            themes={themes}
                            withThemeSwitch={allowThemeSwitch}
                        />
                        {instagram && (
                            <a
                                href={instagram.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Instagram"
                                aria-label="Instagram"
                                className="nav-icon text-color-1 ml-6 pl-6 border-l border-medium"
                            >
                                <Instagram size={17} strokeWidth={1.4} />
                            </a>
                        )}
                    </div>

                    {/* Keeps the logo optically centred on small screens */}
                    <span className="w-6 lg:hidden" aria-hidden="true"></span>
                </div>
                <div className="absolute line h-px left-0 bottom-0 bg-medium"></div>
            </header>

            <SideBar open={sidebarOpen} onChange={setSidebarOpen}>
                <div className="sidebar-panel bg-bg h-full flex flex-col justify-center relative px-8">
                    <button
                        className="absolute top-0 right-0 m-6 text-color-2 outline-none"
                        aria-label="Zamknij menu"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <X size={22} strokeWidth={1.25} />
                    </button>
                    <div className="absolute top-0 left-0 m-8">
                        <Link
                            to="/"
                            title={data.site.siteMetadata.title}
                            className="inline-block"
                        >
                            <Logo className="brand-mark w-16" />
                        </Link>
                    </div>
                    <div>
                        <List
                            name="sidebar-nav"
                            className="nav-links"
                            current={currentLocation}
                            currentTheme={currentTheme}
                            switchTheme={switchTheme}
                            themes={themes}
                            withThemeSwitch={allowThemeSwitch}
                            liClassName="block"
                        />
                        {instagram && (
                            <a
                                href={instagram.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-btn text-color-1 mt-10"
                                aria-label="Instagram"
                            >
                                <Instagram strokeWidth={1.4} />
                            </a>
                        )}
                    </div>
                </div>
            </SideBar>

            {navPlaceholder && (
                <div style={{ height: `${navbarHeight}px` }}></div>
            )}
        </React.Fragment>
    )
}

export default Navbar
